import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Question } from "@phosphor-icons/react";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { faqRich, faqPlain } from "@/lib/faqCategories";
import FaqMedia from "@/components/FaqMedia";

const SITE = process.env.REACT_APP_BACKEND_URL || "";

// "Was this helpful?" — one vote per FAQ per browser; No asks what was missing.
function HelpfulVote({ slug }) {
  const key = `rf_faq_vote_${slug}`;
  const [state, setState] = useState(() => (localStorage.getItem(key) ? "done" : "ask"));
  const [reason, setReason] = useState("");
  const send = async (helpful, why = "") => {
    try { await api.post(`/faqs/by-slug/${slug}/vote`, { helpful, reason: why }); } catch { /* still thank them */ }
    localStorage.setItem(key, helpful ? "yes" : "no");
    setState("done");
  };
  if (state === "done") return <p className="mt-8 text-sm font-bold text-rf-ink-soft" data-testid="faq-vote-thanks">Thanks for your feedback!</p>;
  return (
    <div className="mt-8 card-recipe !p-5" data-testid="faq-vote">
      {state === "ask" ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-bold text-rf-ink mr-2">Was this helpful?</p>
          <button type="button" onClick={() => send(true)} className="btn-outline-ink !py-2 !px-5 text-sm" data-testid="faq-vote-yes">Yes</button>
          <button type="button" onClick={() => setState("why")} className="btn-outline-ink !py-2 !px-5 text-sm" data-testid="faq-vote-no">No</button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-bold text-rf-ink">Sorry about that — what was missing?</p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className="input-rf w-full min-h-[80px]"
            placeholder="e.g. I couldn't find the button on my phone" data-testid="faq-vote-reason" />
          <div className="flex gap-2">
            <button type="button" onClick={() => send(false, reason)} className="btn-amber !py-2 !px-5 text-sm" data-testid="faq-vote-send">Send</button>
            <button type="button" onClick={() => send(false)} className="btn-outline-ink !py-2 !px-5 text-sm" data-testid="faq-vote-skip">Skip</button>
          </div>
        </div>
      )}
    </div>
  );
}

// One page per FAQ entry (SEO): question, answer, related questions from the same heading.
export default function FAQEntry() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [free, setFree] = useState("5");

  useEffect(() => {
    setData(null); setMissing(false);
    api.get(`/faqs/by-slug/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
    api.get("/settings").then((r) => setFree(String(r.data?.free_recipe_limit || 5))).catch(() => {});
  }, [slug]);

  const t = (s) => (s || "").replace(/\{FREE\}/g, free);

  if (missing) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="faq-entry-missing">
        <PageMeta title="Question not found — FAQs" />
        <h1 className="font-display text-3xl font-black text-rf-ink mb-4">Question not found</h1>
        <Link to="/faqs" className="btn-amber inline-flex items-center gap-2"><ArrowLeft size={18} weight="bold" /> All FAQs</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" /></div>;
  }

  const { faq, related, seo } = data;
  const q = t(faq.question);
  const a = faqPlain(t(faq.answer));
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="faq-entry-page">
      <PageMeta
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={`/faqs/${faq.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [{ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } }],
        }}
      />
      <BreadcrumbJsonLd items={[{ name: "Home", url: `${SITE}/` }, { name: "FAQs", url: `${SITE}/faqs` }, { name: q }]} />
      <nav className="text-xs text-rf-ink-soft mb-6" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-rf-orange">Home</Link><span className="mx-1.5">›</span>
        <Link to="/faqs" className="hover:text-rf-orange">FAQs</Link><span className="mx-1.5">›</span>
        <span className="text-rf-ink font-bold">{faq.category}</span>
      </nav>
      <p className="overline mb-3 inline-flex items-center gap-2" data-testid="faq-entry-category"><Question size={16} weight="duotone" /> {faq.category}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-rf-ink leading-[1.05] mb-6" data-testid="faq-entry-question">{q}</h1>
      <h2 className="overline mb-2">Answer</h2>
      <div className="card-recipe text-lg text-rf-ink leading-relaxed whitespace-pre-line" data-testid="faq-entry-answer">{faqRich(t(faq.answer))}</div>
      <FaqMedia media={faq.media} alt={q} testid="faq-entry-media" />

      <HelpfulVote slug={faq.slug} />

      {related.length > 0 && (
        <section className="mt-12" data-testid="faq-entry-related">
          <h2 className="font-display text-2xl font-black text-rf-ink mb-4">Related questions about {faq.category}</h2>
          <ul className="space-y-2">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to={`/faqs/${r.slug}`} data-testid={`faq-related-${r.slug}`}
                  className="block rounded-2xl border border-rf-ink/10 bg-white px-5 py-3 font-bold text-rf-ink hover:border-rf-orange hover:text-rf-orange transition-colors">
                  {t(r.question)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <Link to="/faqs" data-testid="faq-entry-all" className="btn-outline-ink inline-flex items-center gap-2 mt-10">
        <ArrowLeft size={18} weight="bold" /> See all FAQs
      </Link>
    </div>
  );
}
